import ModalDelegate from 'store/general/modalDelegate';
import { action, computed, makeObservable, observable, ObservableMap, override, runInAction } from 'mobx';
import { FamilyMember, FamilyCensus, ConditionMember } from 'store/modules/subscriber-imq/types';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { TabItem } from '@insurancemenu/imenu-components-lib/dist/types/HorizontalTabBar';
import { removeIMQMedicalConditionMember, saveIMQMedicalConditionAnswers } from 'api/operations/electionService';
import { Primitive } from 'types/general';
import { validateDateThatNotInFuture } from '@insurancemenu/imenu-components-lib/dist/helpers/validateDateOfBirth';
import { ValidationRule } from '@insurancemenu/imenu-components-lib/dist/utils/forms/validationsMapping';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types/Toast';

export const NEW_FAMILY_MEMBER_TAB_ID = 'new-family-member';

const isDateNotInFuture = (value: string): boolean => validateDateThatNotInFuture(value);

const dateNotInFutureValidation: ValidationRule = {
  check: isDateNotInFuture,
  msg: 'Date cannot be in the future.',
};

const dateFieldValidations = {
  customValidations: [dateNotInFutureValidation],
};

export interface ManageConditionFormFields extends DefaultFormFields {
  memberId: FormControl<string[]>;
  dateDiagnosed: FormControl<string>;
  dateTreated: FormControl<string>;
  treatment: FormControl<string>;
  medication: FormControl<string>;
  isTreatmentOngoing: FormControl<string>;
  currentOrFutureTreatment: FormControl<string>;
}

const createMemberFormFields = (memberId: string, isMemberIdDisabled = false): ManageConditionFormFields => ({
  memberId: new FormControl<string[]>([memberId], isMemberIdDisabled ? { disabled: true } : {}),
  dateDiagnosed: new FormControl<string>('', dateFieldValidations),
  dateTreated: new FormControl<string>('', dateFieldValidations),
  treatment: new FormControl<string>('', { validations: ['printableCharacters'] }),
  medication: new FormControl<string>('', { validations: ['printableCharacters'] }),
  isTreatmentOngoing: new FormControl<string>('false'),
  currentOrFutureTreatment: new FormControl<string>('', {
    enabledWithFields: ['isTreatmentOngoing'],
    validations: ['printableCharacters'],
  }),
});

interface DomainParams {
  subscriberId: string;
  refreshData: () => Promise<void>;
}

export class ManageConditionModalDelegate extends ModalDelegate {
  private _category: string = '';
  private _conditionName: string = '';
  private _affectedPeople: FamilyMember[] = [];
  private _familyCensus: FamilyCensus = {};
  private _reportedMemberForms: ObservableMap<string, FormController<ManageConditionFormFields>> = observable.map({});
  private _newMemberForm: FormController<ManageConditionFormFields> | null = null;
  private _activeMemberId: string = '';
  private _subscriberId: string = '';
  private _refreshData: () => Promise<void>;
  private _needsRefreshOnClose: boolean = false;

  public get activeMemberId(): string {
    return this._activeMemberId;
  }

  public get conditionName(): string {
    return this._conditionName;
  }

  public get reportedMemberForms(): ObservableMap<string, FormController<ManageConditionFormFields>> {
    return this._reportedMemberForms;
  }

  public get newMemberForm(): FormController<ManageConditionFormFields> | null {
    return this._newMemberForm;
  }

  public get familyCensus(): FamilyCensus {
    return this._familyCensus;
  }

  public get memberTabs(): TabItem[] {
    const tabs: TabItem[] = Array.from(this._reportedMemberForms.keys()).map((memberId) => ({
      id: memberId,
      label: this._familyCensus[memberId]?.name ?? '',
    }));

    if (this._newMemberForm) {
      tabs.push({
        id: NEW_FAMILY_MEMBER_TAB_ID,
        label: 'New Family Member',
      });
    }

    return tabs;
  }

  public get activeMemberSelectIds(): string[] {
    if (this._activeMemberId === NEW_FAMILY_MEMBER_TAB_ID) {
      return this.unreportedMembers.map((member) => member.id);
    }

    return this._activeMemberId ? [this._activeMemberId] : [];
  }

  public get activeMemberForm(): FormController<ManageConditionFormFields> | null {
    if (this._activeMemberId === NEW_FAMILY_MEMBER_TAB_ID) {
      return this._newMemberForm;
    }

    const existingMemberForm = this._reportedMemberForms.get(this._activeMemberId);
    if (existingMemberForm) {
      return existingMemberForm;
    }

    return null;
  }

  public get unreportedMembers(): FamilyMember[] {
    return Object.entries(this._familyCensus)
      .filter(([id]) => !this._affectedPeople.some((member) => member.id === id))
      .map(([id, { name }]) => ({ id, name, answers: {} }));
  }

  public get allMembersReported(): boolean {
    return this._reportedMemberForms.size + Number(!!this._newMemberForm) === Object.keys(this._familyCensus).length;
  }

  public get cannotAddNewMemberReason(): string {
    if (this.allMembersReported) {
      return 'All members have been reported.';
    }
    if (this.newMemberForm) {
      return `Complete this family member's details before adding another.`;
    }
    return '';
  }

  public get canSubmitCurrentForm(): boolean {
    if (this._reportedMemberForms.size === 0 && this._newMemberForm !== null && !this._newMemberForm.isFormValid()) {
      return false;
    }

    if (this._activeMemberId === NEW_FAMILY_MEMBER_TAB_ID) {
      return this.newMemberForm?.isFormValid() ?? false;
    }

    const existingMemberForm = this._reportedMemberForms.get(this._activeMemberId);
    if (existingMemberForm) {
      return existingMemberForm.isFormValid();
    }

    return this.newMemberForm?.isFormValid() ?? false;
  }

  public setActiveMemberId(memberId: string): void {
    this._activeMemberId = memberId;
  }

  public createReportedMemberForm(memberId: string, answers: Record<string, Primitive>): void {
    const form = FormController.create<ManageConditionFormFields, []>(createMemberFormFields(memberId, true), {});
    form.populateFields(answers);
    this._reportedMemberForms.set(memberId, form);
  }

  public addNewMemberForm(): void {
    const newMember = this.unreportedMembers[0];
    const form = FormController.create<ManageConditionFormFields, []>(createMemberFormFields(newMember.id, false), {});
    this._newMemberForm = form;
    this._activeMemberId = NEW_FAMILY_MEMBER_TAB_ID;
  }

  public async deleteMemberForm(memberId: string): Promise<void> {
    this.setIsLoading(true);
    try {
      await this._removeMember(memberId);
      runInAction(() => {
        this._needsRefreshOnClose = true;
        this.close();
      });
    } finally {
      runInAction(() => {
        this.setIsLoading(false);
      });
    }
  }

  private _setInitialActiveMemberId(): void {
    if (this._newMemberForm) {
      this._activeMemberId = NEW_FAMILY_MEMBER_TAB_ID;
      return;
    }
    const existingMemberForms = Array.from(this._reportedMemberForms.keys());
    this._activeMemberId = existingMemberForms[existingMemberForms.length - 1] ?? '';
  }

  public resetData(): void {
    this._conditionName = '';
    this._affectedPeople = [];
    this._familyCensus = {};
    this._reportedMemberForms.clear();
    this._newMemberForm = null;
    this._activeMemberId = '';
    this._needsRefreshOnClose = false;
  }

  public setupModalData(
    category: string,
    conditionName: string,
    affectedPeople: FamilyMember[],
    familyCensus: FamilyCensus,
  ): void {
    this._category = category;
    this._conditionName = conditionName;
    this._affectedPeople = affectedPeople;
    this._familyCensus = familyCensus;
    this._reportedMemberForms.clear();
    this._newMemberForm = null;
    affectedPeople.forEach((member) => {
      if (Object.keys(member.answers).length > 0) {
        this.createReportedMemberForm(member.id, member.answers);
      }
    });

    const hasReportedForms = this._reportedMemberForms.size > 0;

    if (!hasReportedForms) {
      this.addNewMemberForm();
    } else {
      this._setInitialActiveMemberId();
    }
  }

  public setupAndOpenModal(
    category: string,
    conditionName: string,
    affectedPeople: FamilyMember[],
    familyCensus: FamilyCensus,
  ): void {
    this.setupModalData(category, conditionName, affectedPeople, familyCensus);
    this.open();
  }

  public override close(): void {
    const shouldRefresh = this._needsRefreshOnClose;
    this.resetData();
    super.close();
    if (shouldRefresh) {
      void this._refreshData();
    }
  }

  private async _removeMember(memberId: string): Promise<ConditionMember[]> {
    const subscriberId = this._subscriberId;
    const category = this._category;
    const conditionName = this._conditionName;

    const { imqAnswers } = await removeIMQMedicalConditionMember(subscriberId, category, conditionName, memberId);

    const medicalConditions = imqAnswers.categories.find((c) => c.name === category)?.medicalConditions ?? [];

    return medicalConditions.find((c) => c.name === conditionName)?.members ?? [];
  }

  private async _saveMember(memberId: string, answers: Record<string, Primitive>): Promise<ConditionMember[]> {
    const subscriberId = this._subscriberId;
    const category = this._category;
    const conditionName = this._conditionName;

    const { imqAnswers } = await saveIMQMedicalConditionAnswers(
      subscriberId,
      category,
      conditionName,
      memberId,
      answers,
    );

    const medicalConditions = imqAnswers.categories.find((c) => c.name === category)?.medicalConditions ?? [];

    return medicalConditions.find((c) => c.name === conditionName)?.members ?? [];
  }

  private async _saveMemberAnswers(
    formController: FormController<ManageConditionFormFields>,
  ): Promise<ConditionMember[]> {
    const memberId = formController.fields.memberId.value[0];

    return this._saveMember(memberId, formController.getFormDataObject() as Record<string, Primitive>);
  }

  public async submitMemberForm(): Promise<void> {
    const activeMemberForm = this.activeMemberForm;
    if (!activeMemberForm) {
      throw new Error('Active member form is not set');
    }

    const category = this._category;
    const conditionName = this._conditionName;
    const familyCensus = this._familyCensus;

    this.setIsLoading(true);
    try {
      const savedMembers = await this._saveMemberAnswers(activeMemberForm);
      runInAction(() => {
        this.setupModalData(
          category,
          conditionName,
          savedMembers.map((member) => ({
            id: member.id,
            name: familyCensus[member.id].name ?? '',
            answers: member.answers,
          })),
          familyCensus,
        );
        this._newMemberForm = null;
        this._needsRefreshOnClose = true;
      });
    } finally {
      runInAction(() => {
        this.setIsLoading(false);
        toastManager.addToast('Data saved successfully', ToastColorTypes.SUCCESS);
      });
    }
  }

  constructor({ subscriberId, refreshData }: DomainParams) {
    super();
    this._subscriberId = subscriberId;
    this._refreshData = refreshData;
    makeObservable<
      ManageConditionModalDelegate,
      | '_category'
      | '_conditionName'
      | '_affectedPeople'
      | '_familyCensus'
      | '_reportedMemberForms'
      | '_newMemberForm'
      | '_activeMemberId'
      | '_subscriberId'
      | '_refreshData'
      | '_needsRefreshOnClose'
      | '_removeMember'
      | '_saveMember'
      | '_saveMemberAnswers'
      | '_setInitialActiveMemberId'
    >(this, {
      _category: observable,
      _conditionName: observable,
      _affectedPeople: observable,
      _familyCensus: observable,
      _reportedMemberForms: false,
      _newMemberForm: observable,
      _activeMemberId: observable,
      _subscriberId: observable,
      _needsRefreshOnClose: observable,
      activeMemberId: computed,
      conditionName: computed,
      reportedMemberForms: computed,
      newMemberForm: computed,
      familyCensus: computed,
      memberTabs: computed,
      activeMemberSelectIds: computed,
      activeMemberForm: computed,
      unreportedMembers: computed,
      allMembersReported: computed,
      cannotAddNewMemberReason: computed,
      canSubmitCurrentForm: computed,
      _refreshData: action.bound,
      setActiveMemberId: action.bound,
      _setInitialActiveMemberId: action.bound,
      createReportedMemberForm: action.bound,
      addNewMemberForm: action.bound,
      resetData: action.bound,
      setupModalData: action.bound,
      setupAndOpenModal: action.bound,
      close: override,
      _removeMember: action.bound,
      _saveMember: action.bound,
      _saveMemberAnswers: action.bound,
      submitMemberForm: action.bound,
      deleteMemberForm: action.bound,
    });
  }
}
